import {
  Box,
  type HTMLChakraProps,
  MenuContent,
  type MenuContentProps,
  MenuItem,
  MenuItemCommand,
  type MenuItemProps,
  MenuPositioner,
  type MenuPositionerProps,
  MenuRoot,
  MenuTrigger,
  type MenuTriggerProps,
  Portal,
  useToken,
} from '@chakra-ui/react';

import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDownIcon } from '@/assets/icons';
import type { ActionMenuItem } from '../FaceliftActionMenu';
import FaceliftButton from '../FaceliftButton';

interface FaceliftSimpleMenuProps extends MenuTriggerProps {
  actionItems: ActionMenuItem[];
  forceOpen?: boolean; // Force menu to stay open (for tutorials)
}

interface FaceliftSimpleMenuTriggerProps extends MenuTriggerProps {
  children: React.ReactNode;
}

interface FaceliftSimpleMenuPositionerProps extends MenuPositionerProps {
  children: React.ReactNode;
}

interface FaceliftSimpleMenuContentProps
  extends MenuContentProps,
  HTMLChakraProps<'div'> {
  children: React.ReactNode;
}

interface FaceliftSimpleMenuItemProps extends MenuItemProps {
  children: React.ReactNode;
  value: string;
}

const CustomMenu = {
  Trigger: (props: FaceliftSimpleMenuTriggerProps) => (
    <MenuTrigger {...props} />
  ),
  Positioner: (props: FaceliftSimpleMenuPositionerProps) => (
    <MenuPositioner {...props} />
  ),
  Content: (props: FaceliftSimpleMenuContentProps) => (
    <MenuContent {...props} />
  ),
  Item: (props: FaceliftSimpleMenuItemProps) => <MenuItem {...props} />,
  ItemCommand: MenuItemCommand,
};

const FaceliftSimpleMenu = (props: FaceliftSimpleMenuProps) => {
  const [brandSolid, brandSecondary] = useToken('colors', [
    'brand.solid',
    'brand.solidSecondary',
  ]);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const lastOpenTimeRef = useRef<number>(0);
  // Use a unique data attribute (id) to mark trigger and content nodes so we
  // can reliably detect clicks inside the menu without forwarding refs.
  const menuId = useId();
  const dataAttr = `data-facelift-menu-${menuId}`;

  const { actionItems, forceOpen = false } = props;
  
  // Force menu open when forceOpen is true
  useEffect(() => {
    if (forceOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [forceOpen, isOpen]);

  const solidGradiant = `linear-gradient(90deg, ${brandSolid} 0%, ${brandSecondary} 100%)`;

  useEffect(() => {
    // Don't close menu if forceOpen is true
    if (!isOpen || forceOpen) return;

    const onDocClick = (ev: MouseEvent) => {
      // Ignore outside close for 150ms after opening — Chakra/focus can fire
      // a mousedown/click right after open, which would close the menu immediately.
      if (Date.now() - lastOpenTimeRef.current < 150) return;

      // Use composedPath to account for Shadow DOM / portals
      const path = ev.composedPath?.() ?? (ev as unknown as { path?: EventTarget[] }).path ?? [];

      for (const node of path) {
        if (!node || !(node instanceof Element)) continue;
        if (node.getAttribute(dataAttr) !== null) {
          // Click happened inside our menu/trigger — ignore
          return;
        }
      }

      setIsOpen(false);
    };

    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [isOpen, dataAttr, forceOpen]);

  // Use forceOpen to control menu state
  const effectiveIsOpen = forceOpen || isOpen;

  return (
    <MenuRoot 
      open={effectiveIsOpen}
      onOpen={() => {
        if (!forceOpen) {
          lastOpenTimeRef.current = Date.now();
          setIsOpen(true);
        }
      }} 
      onClose={() => {
        if (!forceOpen) {
          setIsOpen(false);
        }
      }}
    >
      <CustomMenu.Trigger>
        <FaceliftButton
          // mark the trigger so our document listener can find clicks inside it
          {...{ [dataAttr]: 'true' }}
          size="xs"
          width="120px"
          bg={solidGradiant}
          color="white"
          rounded={'md'}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => {
            if (!forceOpen) {
              if (!isOpen) lastOpenTimeRef.current = Date.now();
              setIsOpen(!isOpen);
            }
          }}
          pl={3}
          _hover={{
            opacity: 0.9,
            transform: 'scale(1.02)',
          }}
          _active={{
            transform: 'scale(0.97)',
          }}
          _focus={{ outline: 'none' }}
        >
          Action
          <Box
            display="inline-flex"
            alignItems={'flex-end'}
            textAlign={'right'}
            transform={effectiveIsOpen ? 'scale(0.6) rotate(180deg)' : 'scale(0.6)'}
            transition="transform 0.2s ease"
          >
            <ChevronDownIcon width={8} height={5} color="currentColor" />
          </Box>
        </FaceliftButton>
      </CustomMenu.Trigger>

      <Portal>
        <CustomMenu.Positioner>

          {actionItems.map((actionItem) => (
            <CustomMenu.Content
              // mark the content so our document listener can find clicks inside it
              {...{ [dataAttr]: 'true' }}
              width="120px"
              minW={0}
              borderRadius="md"
              boxShadow="ms"
              key={actionItem.title}
              onClick={() => {
                actionItem?.action?.();
              }}
              p={1}
            >
              <CustomMenu.Item value={actionItem.title}  >
                {actionItem.title}
              </CustomMenu.Item>
            </CustomMenu.Content>
          ))}
        </CustomMenu.Positioner>
      </Portal>
    </MenuRoot>
  );
};

export default FaceliftSimpleMenu;
